/**
 * Centralised WhatsApp deep-link builder.
 *
 * Every public "Chat on WhatsApp" / "Enquire on WhatsApp" CTA on the
 * site MUST route through this helper so the prefilled greeting stays
 * consistent (no personal-name greetings like "Rakshit ji"; brand-led
 * "Hi Samrat Glass Emporium, …" everywhere).
 *
 * Admin-only tools that build their own contextual messages (e.g. the
 * admin "reply to enquiry" quick action) are exempt — they're not
 * public and their message is per-customer.
 */

const BRAND_PREFIX = "Hi Samrat Glass Emporium,";
const PUBLIC_SITE_ORIGIN = "https://samratglass.com";

/**
 * Named prefilled messages for each public entry point. Kept in an
 * exported object so unit tests can pin the exact wording.
 */
export const WA_MESSAGES = {
  general:
    `${BRAND_PREFIX} I would like to know more about your lighting collection.`,
  customLighting:
    `${BRAND_PREFIX} I would like to discuss a custom lighting / bulk order requirement.`,
  architects:
    `${BRAND_PREFIX} I would like to discuss a lighting requirement for an architecture/interior project.`,
  gallery:
    `${BRAND_PREFIX} I would like to know more about this project/style and the lighting used in it.`,
  notFound:
    `${BRAND_PREFIX} I landed on a page that could not be found — can you help me find the piece I was looking at?`,
  imageSearch:
    `${BRAND_PREFIX} I used image search but have not yet found the exact light I have in mind. I will share the reference photo here. Please help me identify the closest catalogue piece or advise whether it can be custom-made.`,
};

const digitsOnly = (n) => String(n || "").replace(/[^0-9]/g, "");

/**
 * Compose the product-page WhatsApp message. Includes the product URL
 * (usually `window.location.href`) if provided so the recipient can
 * jump straight to the piece.
 */
export const productMessage = (product, url) => {
  const nameSku = `${product?.name || "a product"}${
    product?.sku ? ` (${product.sku})` : ""
  }`;
  const base = `${BRAND_PREFIX} I am interested in ${nameSku}. Please share more details.`;
  return url ? `${base}\n${url}` : base;
};

/**
 * Compose an "actual product" request. This keeps the request specific
 * to the exact catalogue reference without promising that a video is
 * available for every product.
 */
export const actualProductMessage = (product, url) => {
  const nameSku = `${product?.name || "this product"}${
    product?.sku ? ` (${product.sku})` : ""
  }`;
  const base = `${BRAND_PREFIX} please share recent live photos of ${nameSku}. If available, a short video would also be helpful.`;
  return url ? `${base}\n${url}` : base;
};

/**
 * Compose the gallery-project product WhatsApp message. Uses the
 * project title as extra context.
 */
export const galleryProductMessage = (product, project) => {
  const nameSku = `${product?.name || "a product"}${
    product?.sku ? ` (SKU: ${product.sku})` : ""
  }`;
  const proj = project?.title ? ` in your "${project.title}" project` : "";
  return `${BRAND_PREFIX} I saw ${nameSku}${proj}. Please share more details.`;
};

/**
 * Compose the inquiry-basket cart WhatsApp message.
 *
 * Each line includes the stable public product URL when an id is available.
 * We intentionally use the production domain even from Preview so a staff
 * member or customer never receives a temporary preview-domain link.
 */
export const cartMessage = (items = [], siteOrigin = PUBLIC_SITE_ORIGIN) => {
  const origin = String(siteOrigin || PUBLIC_SITE_ORIGIN).replace(/\/$/, "");
  const lines = items.map((i) => {
    const summary = `- ${i.name}${i.sku ? ` (SKU: ${i.sku})` : ""} (x${i.quantity})`;
    if (!i.product_id) return summary;
    return `${summary}\n  ${origin}/product/${encodeURIComponent(i.product_id)}`;
  });
  return `${BRAND_PREFIX} I would like to enquire about the following items:\n${lines.join("\n")}`;
};

/**
 * Build a wa.me deep link with the given phone number and prefilled
 * message. Returns "" if no number is configured so callers can hide
 * the CTA gracefully.
 */
export const buildWaLink = (number, message) => {
  const digits = digitsOnly(number);
  if (!digits) return "";
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

// -----------------------------------------------------------------------
// Convenience wrappers used across pages/components.
// -----------------------------------------------------------------------
export const waGeneralLink = (number) => buildWaLink(number, WA_MESSAGES.general);
export const waCustomLightingLink = (number) => buildWaLink(number, WA_MESSAGES.customLighting);
export const waArchitectsLink = (number) => buildWaLink(number, WA_MESSAGES.architects);
export const waGalleryLink = (number) => buildWaLink(number, WA_MESSAGES.gallery);
export const waNotFoundLink = (number) => buildWaLink(number, WA_MESSAGES.notFound);
export const waImageSearchLink = (number) => buildWaLink(number, WA_MESSAGES.imageSearch);
export const waProductLink = (number, product, url) =>
  buildWaLink(number, productMessage(product, url));
export const waActualProductLink = (number, product, url) =>
  buildWaLink(number, actualProductMessage(product, url));
export const waGalleryProductLink = (number, product, project) =>
  buildWaLink(number, galleryProductMessage(product, project));
export const waCartLink = (number, items, siteOrigin) =>
  buildWaLink(number, cartMessage(items, siteOrigin));
